import React, { useState } from 'react';
import { Home, Package, CheckSquare, Layers, FileText, User, Menu, X, LogOut, Building, Box, Flame, Sparkles, Palette } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from './V2Components';
import { FEATURES } from '../../featureFlags';

/**
 * Layout Component (V2)
 * The main layout wrapper for the application's V2 pages.
 * Includes a responsive sidebar navigation, mobile header, and main content area.
 * Uses tonal layering (no borders) per the Hestia design system.
 */
const Layout = ({ children, onLogout, fullWidthContent = false }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const NavItem = ({ path, icon: Icon, label }) => {
        const isActive = location.pathname === path ||
            (path === '/spaces' && location.pathname.startsWith('/room/'));

        return (
            <button
                onClick={() => {
                    navigate(path);
                    setMobileMenuOpen(false);
                }}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-xl transition-all duration-200 ${isActive
                    ? 'bg-[var(--bg-card)] text-[var(--primary)] font-semibold shadow-[var(--shadow-sm)]'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-main)] hover:bg-[var(--bg-card)] hover:translate-x-1'
                    }`}
            >
                <Icon size={18} strokeWidth={isActive ? 2.5 : 1.5} />
                <span className="text-sm">{label}</span>
            </button>
        );
    };

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] font-body transition-colors duration-300">
            {/* Mobile Header */}
            <header className="lg:hidden p-4 flex justify-between items-center glass-header sticky top-0 z-30">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] rounded-xl flex items-center justify-center">
                        <Flame size={16} className="text-white" />
                    </div>
                    <div>
                        <span className="font-headline font-bold text-base tracking-tight">Hestia</span>
                    </div>
                </div>
                <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2 text-[var(--text-main)] hover:bg-[var(--bg-hover)] rounded-xl"
                    aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                >
                    {mobileMenuOpen ? <X /> : <Menu />}
                </button>
            </header>

            <div className="flex h-screen overflow-hidden">
                {/* Mobile overlay */}
                {mobileMenuOpen && (
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                )}

                {/* Sidebar Navigation — tonal bg shift, no border */}
                <aside className={`
                    fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[var(--bg-hover)] transform transition-transform duration-300 ease-in-out
                    ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                `}>
                    <div className="p-5 flex flex-col h-full">
                        {/* Branding */}
                        <div className="flex items-center gap-3 mb-8 hidden lg:flex">
                            <div className="w-9 h-9 bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] rounded-xl flex items-center justify-center shadow-lg shadow-[rgba(3,37,33,0.15)]">
                                <Flame size={18} className="text-white" />
                            </div>
                            <div className="flex flex-col">
                                <span className="font-headline font-bold text-lg tracking-tight leading-tight">Hestia</span>
                            </div>
                        </div>

                        <nav className="flex-1 space-y-1">
                            <NavItem path="/dashboard" icon={Building} label="Dashboard" />
                            <NavItem path="/houses" icon={Home} label="Houses" />
                            <NavItem path="/spaces" icon={Layers} label="Rooms" />
                            <NavItem path="/inventory" icon={Package} label="Inventory" />
                            <NavItem path="/paints" icon={Palette} label="Paints" />
                            {FEATURES.maintenance && <NavItem path="/maintenance" icon={CheckSquare} label="Maintenance" />}
                            <NavItem path="/documents" icon={FileText} label="Documents" />
                            {FEATURES.models3d && <NavItem path="/models3d" icon={Box} label="3D Models" />}
                        </nav>

                        <div className="pt-5 space-y-1">
                            <NavItem path="/subscription" icon={Sparkles} label="Plan" />
                            <NavItem path="/profile" icon={User} label="Profile" />
                            <button
                                onClick={onLogout}
                                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[var(--text-muted)] hover:text-red-500 hover:bg-red-50/50 transition-all duration-200 text-sm"
                            >
                                <LogOut size={18} strokeWidth={1.5} />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 overflow-auto bg-[var(--bg-main)] theme-transition relative">
                    {/* p-4 below `lg`: 32px of horizontal padding on a phone left
                        content strips (tab bars, filter rows) without room to fit.
                        Matches design.md's `p-4 lg:p-8` prescription. */}
                    <div className={fullWidthContent ? 'p-4 lg:p-8 w-full' : 'p-4 lg:p-8 max-w-7xl mx-auto'}>
                        {children}
                    </div>
                    <ToastContainer />
                </main>
            </div>
        </div>
    );
};

export default Layout;
